<template>
  <div class="product-detail">
    <!-- Product header -->
    <div class="product-header">
      <div class="container">
        <nav class="breadcrumb">
          <router-link to="/">Home</router-link> /
          <router-link to="/products">Products</router-link> /
          <span>{{ product.name }}</span>
        </nav>
        <div class="product-intro">
          <div class="product-images">
            <div class="main-image">
              <img :src="product.mainImage" :alt="product.name" />
            </div>
            <div class="image-gallery">
              <img v-for="(img, index) in product.gallery" :key="index" :src="img" :alt="product.name" />
            </div>
          </div>
          <div class="product-info">
            <h1 class="product-title">{{ product.name }}</h1>
            <p class="product-subtitle">{{ product.subtitle }}</p>
            <div class="product-description">{{ product.description }}</div>
            <div class="product-highlights">
              <div v-for="highlight in product.highlights" :key="highlight" class="highlight-item">
                <span class="highlight-icon">✓</span>
                {{ highlight }}
              </div>
            </div>
            <div class="product-actions">
              <button class="btn-primary">Learn More</button>
              <button class="btn-secondary">Contact</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product features -->
    <div class="product-features">
      <div class="container">
        <h2 class="section-title">Product Features</h2>
        <div class="features-grid">
          <div v-for="feature in product.features" :key="feature.title" class="feature-card">
            <div class="feature-icon">{{ feature.icon }}</div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Technical specifications -->
    <div class="product-specs">
      <div class="container">
        <h2 class="section-title">Technical Specifications</h2>
        <div class="specs-grid">
          <div v-for="spec in product.specifications" :key="spec.category" class="spec-category">
            <h3>{{ spec.category }}</h3>
            <div class="spec-list">
              <div v-for="item in spec.items" :key="item.name" class="spec-item">
                <span class="spec-name">{{ item.name }}</span>
                <span class="spec-value">{{ item.value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Related resources -->
    <div class="product-resources">
      <div class="container">
        <h2 class="section-title">Related Resources</h2>
        <div class="resources-grid">
          <div v-for="resource in product.resources" :key="resource.type" class="resource-card">
            <div class="resource-icon">{{ resource.icon }}</div>
            <h3>{{ resource.title }}</h3>
            <p>{{ resource.description }}</p>
            <a :href="resource.link" class="resource-link">Download</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Product video -->
    <div class="product-video">
      <div class="container">
        <h2 class="section-title">Product Video</h2>
        <div class="video-container">
          <div class="video-placeholder">
            <div class="video-overlay">
              <div class="play-button">▶</div>
              <p>Click to watch product demo video</p>
            </div>
            <img :src="product.videoThumbnail" :alt="product.name + ' demo video'" />
          </div>
          <div class="video-info">
            <h3>{{ product.name }} Product Intro</h3>
            <p>Learn more about detailed functions and usage via video, including smart control, comfort seating, and safety protection.</p>
            <div class="video-features">
              <span class="video-feature">Product Demo</span>
              <span class="video-feature">Feature Highlights</span>
              <span class="video-feature">User Guide</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product styles -->
    <div class="product-stylish">
      <div class="container">
        <h2 class="section-title">Product Styles</h2>
        <div class="stylish-content">
          <div class="stylish-intro">
            <h3>Premium Color Options</h3>
            <p>With options like chrome accents, aggressive tread and a variety of trim colours.</p>
          </div>
          <div class="color-options">
            <div v-for="color in product.colorOptions" :key="color.name" class="color-option">
              <div class="color-preview" :style="{ backgroundColor: color.value }"></div>
              <h4>{{ color.name }}</h4>
              <p>{{ color.description }}</p>
            </div>
          </div>
          <div class="trim-options">
            <h3>Trim Options</h3>
            <div class="trim-grid">
              <div v-for="trim in product.trimOptions" :key="trim.name" class="trim-option">
                <img :src="trim.image" :alt="trim.name" />
                <h4>{{ trim.name }}</h4>
                <p>{{ trim.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recommended accessories -->
    <div class="product-accessories">
      <div class="container">
        <h2 class="section-title">Recommended Accessories</h2>
        <div class="accessories-grid">
          <div v-for="accessory in product.accessories" :key="accessory.name" class="accessory-card">
            <img :src="accessory.image" :alt="accessory.name" />
            <h3>{{ accessory.name }}</h3>
            <p>{{ accessory.description }}</p>
            <span class="accessory-price">{{ typeof accessory.price === 'number' ? '$' + accessory.price : accessory.price }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductDetail',
  data() {
    return {
      product: {
        name: 'BAW01 – Classic Wheelchair',
        subtitle: 'Reliable & Durable',
        description: 'The BAW01 Classic Wheelchair features electromagnetic brake system, dual 220W motors, and 12 Ah battery with 20 Ah option. Perfect for daily mobility with 18±5 km range and foldable design.',
        mainImage: 'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/swc1.png',
        gallery: [
          'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/swc1.png',
          'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w1.png',
          'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w2.png',
          'https://onbotics-map-img.oss-cn-shanghai.aliyuncs.com/w3.png'
        ],
        highlights: [
          'Electromagnetic brake system',
          'Dual 220W motors',
          '12 Ah battery (20 Ah optional)',
          '18±5 km range',
          'Foldable design',
          'Load capacity up to 120 kg'
        ],
        features: [
          {
            icon: '⚡',
            title: 'Powerful Performance',
            description: 'Dual 220W motors provide reliable power for various terrains and conditions.'
          },
          {
            icon: '🔋',
            title: 'Long Range',
            description: '12 Ah battery delivers 18±5 km range, with 20 Ah option for extended travel.'
          },
          {
            icon: '🛡️',
            title: 'Safety First',
            description: 'Electromagnetic brake system ensures safe and reliable stopping power.'
          },
          {
            icon: '📦',
            title: 'Portable Design',
            description: 'Foldable frame makes storage and transport convenient.'
          }
        ],
        specifications: [
          {
            category: 'Performance',
            items: [
              { name: 'Weight Capacity 载重', value: '120kgs/265lbs' },
              { name: 'Charge time 充电时间', value: '6H' },
              { name: 'Max. forward speed 最大前进速度', value: 'Up to 6km/h' },
              { name: 'Max.backward speed 最大后退速度', value: 'Up to 6km/h' },
              { name: 'Mini Turning radius 最小转弯半径', value: '<1200mm' },
              { name: 'Climbing capability 爬坡能力', value: '≤9°' },
              { name: 'Obstacle clearance height越障高度', value: '50mm' },
              { name: 'Ditch crossing width 越沟宽度', value: '200mm' },
              { name: 'Driving Range 行驶距离', value: 'UP to 30km' },
              { name: 'Ground clearance 离地间隙', value: '80mm' }
            ]
          },
          {
            category: 'Tyres',
            items: [
              { name: 'Front Tyres 前轮', value: '7inch 170mm solid' },
              { name: 'Rear Tyres 后轮', value: '8inch 210mm solid' },
              { name: 'Anti-tip Tyres 防倾轮', value: 'Standard' },
              { name: 'Rim Material 轮毂材质', value: 'Aluminum alloy' }
            ]
          },
          {
            category: 'Seat',
            items: [
              { name: 'Seat Material 座垫材质', value: 'High-density foam' },
              { name: 'Seat Width 座宽', value: '460mm' },
              { name: 'Seat Deepth 座深', value: '410mm' },
              { name: 'Seat Thickness 厚度', value: '80mm' },
              { name: 'Backrest Height 靠背高度', value: '540mm' },
              { name: 'Seat to Ground Height 座椅离地高度', value: '500mm' },
              { name: 'Seat to Foot platformm Height 座椅离脚踏高度', value: '400mm' }
            ]
          }
        ],
        resources: [
          {
            icon: '📋',
            title: 'Order Form',
            description: 'BAW01 Classic Order Form',
            link: '#'
          },
          {
            icon: '📖',
            title: 'User Manual',
            description: 'BAW01 Classic User Manual',
            link: '#'
          },
          {
            icon: '📄',
            title: 'Product Brochure',
            description: 'BAW01 Classic Brochure',
            link: '#'
          },
          {
            icon: '💰',
            title: 'Pricing Guide',
            description: 'BAW01 Classic Pricing Guide',
            link: '#'
          }
        ],
        videoThumbnail: 'https://via.placeholder.com/800x450?text=BAW01+Demo+Video',
        colorOptions: [
          {
            name: 'Classic Black',
            value: '#000000',
            description: 'Classic black finish for professional appearance'
          },
          {
            name: 'Silver',
            value: '#C0C0C0',
            description: 'Modern silver finish for contemporary look'
          },
          {
            name: 'Blue',
            value: '#2c5aa0',
            description: 'Professional blue for medical environments'
          },
          {
            name: 'White',
            value: '#FFFFFF',
            description: 'Clean white finish for easy maintenance'
          }
        ],
        trimOptions: [
          {
            name: 'Standard Trim',
            image: 'https://via.placeholder.com/200x150?text=Standard+Trim',
            description: 'Standard upholstery with durable materials'
          },
          {
            name: 'Premium Trim',
            image: 'https://via.placeholder.com/200x150?text=Premium+Trim',
            description: 'Premium upholstery with enhanced comfort'
          },
          {
            name: 'Medical Grade',
            image: 'https://via.placeholder.com/200x150?text=Medical+Grade',
            description: 'Medical grade materials for healthcare settings'
          }
        ],
        accessories: [
          {
            name: 'Extended Battery',
            description: '20 Ah battery for extended range',
            image: 'https://via.placeholder.com/150x100?text=Extended+Battery',
            price: 150
          },
          {
            name: 'Headrest',
            description: 'Optional headrest for additional comfort',
            image: 'https://via.placeholder.com/150x100?text=Headrest',
            price: 80
          },
          {
            name: 'Carrying Case',
            description: 'Protective case for transport and storage',
            image: 'https://via.placeholder.com/150x100?text=Carrying+Case',
            price: 60
          }
        ]
      }
    }
  }
}
</script>

<style scoped>
.product-detail {
  min-height: 100vh;
  background: #f8f9fa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.product-header {
  background: white;
  padding: 2rem 0;
  margin-top: 80px;
}

.breadcrumb {
  margin-bottom: 2rem;
  color: #666;
}

.breadcrumb a {
  color: #2c5aa0;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.product-intro {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: start;
}

.product-images {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.main-image img {
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.image-gallery {
  display: flex;
  gap: 0.5rem;
}

.image-gallery img {
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.3s ease;
}

.image-gallery img:hover {
  opacity: 0.8;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.product-title {
  font-size: 2.5rem;
  color: #333;
  margin: 0;
  font-weight: 300;
}

.product-subtitle {
  font-size: 1.3rem;
  color: #2c5aa0;
  margin: 0;
  font-weight: 500;
}

.product-description {
  color: #666;
  line-height: 1.6;
  font-size: 1.1rem;
}

.product-highlights {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #2c5aa0;
  font-weight: 500;
}

.highlight-icon {
  color: #4CAF50;
  font-weight: bold;
}

.product-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.btn-primary, .btn-secondary {
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
}

.btn-secondary {
  background: white;
  color: #2c5aa0;
  border: 2px solid #2c5aa0;
}

.btn-primary:hover, .btn-secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

.product-features, .product-specs, .product-resources, .product-video, .product-stylish, .product-accessories {
  padding: 4rem 0;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 3rem;
  font-weight: 300;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.feature-card h3 {
  color: #2c5aa0;
  margin-bottom: 1rem;
  font-size: 1.3rem;
}

.feature-card p {
  color: #666;
  line-height: 1.6;
}

.specs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.spec-category {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.spec-category h3 {
  color: #2c5aa0;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
  border-bottom: 2px solid #e3f2fd;
  padding-bottom: 0.5rem;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 0.8rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.spec-name {
  color: #666;
  font-weight: 500;
}

.spec-value {
  color: #2c5aa0;
  font-weight: 600;
}

.resources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.resource-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.3s ease;
}

.resource-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.resource-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.resource-card h3 {
  color: #2c5aa0;
  margin-bottom: 0.5rem;
}

.resource-card p {
  color: #666;
  margin-bottom: 1rem;
}

.resource-link {
  display: inline-block;
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  padding: 0.6rem 1.5rem;
  border-radius: 20px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.resource-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

.accessories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.accessory-card {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.3s ease;
}

.accessory-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.accessory-card img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.accessory-card h3 {
  color: #2c5aa0;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.accessory-card p {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.accessory-price {
  color: #2c5aa0;
  font-weight: 600;
  font-size: 1.1rem;
}

/* 视频模块样式 */
.product-video {
  background: white;
}

.video-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  align-items: start;
}

.video-placeholder {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0,0,0,0.15);
  cursor: pointer;
  transition: all 0.3s ease;
}

.video-placeholder:hover {
  transform: scale(1.02);
  box-shadow: 0 12px 40px rgba(0,0,0,0.2);
}

.video-placeholder img {
  width: 100%;
  height: auto;
  display: block;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
}

.play-button {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #2c5aa0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
}

.video-overlay p {
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
}

.video-info h3 {
  color: #2c5aa0;
  font-size: 1.8rem;
  margin-bottom: 1rem;
  font-weight: 300;
}

.video-info p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.video-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.video-feature {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

/* 样式模块样式 */
.product-stylish {
  background: #f8f9fa;
}

.stylish-content {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.stylish-intro {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.stylish-intro h3 {
  color: #2c5aa0;
  font-size: 2rem;
  margin-bottom: 1rem;
  font-weight: 300;
}

.stylish-intro p {
  color: #666;
  font-size: 1.1rem;
  line-height: 1.6;
}

.color-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
}

.color-option {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.3s ease;
}

.color-option:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.color-preview {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1rem;
  border: 3px solid #f0f0f0;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.color-option h4 {
  color: #2c5aa0;
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
}

.color-option p {
  color: #666;
  font-size: 0.9rem;
}

.trim-options h3 {
  color: #2c5aa0;
  font-size: 1.8rem;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 300;
}

.trim-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.trim-option {
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  text-align: center;
  transition: all 0.3s ease;
}

.trim-option:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.trim-option img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.trim-option h4 {
  color: #2c5aa0;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.trim-option p {
  color: #666;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .product-intro {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .product-title {
    font-size: 2rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .features-grid, .specs-grid, .resources-grid, .accessories-grid {
    grid-template-columns: 1fr;
  }
  
  .video-container {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .color-options, .trim-grid {
    grid-template-columns: 1fr;
  }
}
</style> 